import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { resolveBranchId } from '../../../../shared/utils/branchUtils'
import {
  createLeavePolicy,
  getLeavePolicies,
  getLeavePolicyById,
  getStaffTypeLookupValues,
  stageLeavePolicyAttachment,
  updateLeavePolicy,
  type StaffTypeLookupItem,
} from '../api/hrmsLeavePolicies.api'
import type {
  CreateLeavePolicyPayload,
  LeavePoliciesQueryParams,
  LeavePolicyDto,
  UpdateLeavePolicyPayload,
} from '../types/hrmsLeavePolicies.types'

export const HRMS_LEAVE_POLICIES_QUERY_KEY = ['hrms', 'leave', 'policies'] as const

export const HRMS_STAFF_TYPE_LOOKUP_QUERY_KEY = (branchId?: string) =>
  ['lookup-values', 'STAFF_TYPE', branchId ?? ''] as const

export const getHrmsLeavePolicyDetailQueryKey = (policyId?: string | null, branchId?: string) =>
  ['hrms', 'leave', 'policies', 'detail', branchId ?? '', policyId ?? ''] as const

export interface StaffTypeOption {
  label: string
  value: string
}

/**
 * Fetch Staff Type lookup options for employee groups.
 * queryKey: ['lookup-values', 'STAFF_TYPE', branchId]
 * Maps: label = displayName || name || valueCode || id, value = id
 */
export function useStaffTypeOptions() {
  const branchId = resolveBranchId() || ''

  const query = useQuery({
    queryKey: HRMS_STAFF_TYPE_LOOKUP_QUERY_KEY(branchId),
    queryFn: getStaffTypeLookupValues,
    staleTime: 5 * 60 * 1000,
  })

  const rawData = query.data || []
  const options = useMemo<StaffTypeOption[]>(() => {
    return rawData.map((item: StaffTypeLookupItem) => ({
      label: item.displayName || item.name || item.valueCode || item.id,
      value: item.id,
    }))
  }, [rawData])

  return {
    options,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

export function useLeavePolicies() {
  const queryClient = useQueryClient()

  // Staff type options
  const {
    options: staffTypeOptions,
    isLoading: isStaffTypesLoading,
    isError: isStaffTypesError,
    error: staffTypesError,
    refetch: refetchStaffTypes,
  } = useStaffTypeOptions()

  // Filter states
  const [searchQuery, setSearchQuery] = useState('')
  const [employeeGroupFilter, setEmployeeGroupFilter] = useState('All')
  const [statusFilter, setStatusFilter] = useState('All Status')

  // Map filters to API query params (omit empty params)
  const queryParams = useMemo<LeavePoliciesQueryParams>(() => {
    const params: LeavePoliciesQueryParams = {}

    if (employeeGroupFilter && employeeGroupFilter !== 'All') {
      params.staffTypeId = employeeGroupFilter
    }

    if (statusFilter === 'Active' || statusFilter === 'ACTIVE') {
      params.status = 'ACTIVE'
    } else if (statusFilter === 'Inactive' || statusFilter === 'INACTIVE') {
      params.status = 'INACTIVE'
    }

    return params
  }, [employeeGroupFilter, statusFilter])

  // Fetch policies with TanStack React Query
  const policiesQuery = useQuery({
    queryKey: [...HRMS_LEAVE_POLICIES_QUERY_KEY, queryParams],
    queryFn: () => getLeavePolicies(queryParams),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  })

  const policies = policiesQuery.data || []

  // Client-side text search
  const filteredPolicies = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return policies

    return policies.filter((policy) => {
      const matchesName = policy.name?.toLowerCase().includes(query)
      const groupName = policy.staffType?.displayName || policy.staffTypeId || ''
      const matchesGroup = groupName.toLowerCase().includes(query)

      return matchesName || matchesGroup
    })
  }, [policies, searchQuery])

  // Summary count
  const totalAssignedPersonnel = useMemo(() => {
    return filteredPolicies.length > 0 ? 486 : 0
  }, [filteredPolicies])

  const handleClearFilters = useCallback(() => {
    setSearchQuery('')
    setEmployeeGroupFilter('All')
    setStatusFilter('All Status')
  }, [])

  // Create policy mutation
  const createPolicyMutation = useMutation({
    mutationFn: (payload: CreateLeavePolicyPayload) => createLeavePolicy(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HRMS_LEAVE_POLICIES_QUERY_KEY })
    },
  })

  // Stage attachment mutation
  const stageAttachmentMutation = useMutation({
    mutationFn: (file: File) => stageLeavePolicyAttachment(file),
  })

  return {
    policies,
    filteredPolicies,
    isLoading: policiesQuery.isLoading,
    isError: policiesQuery.isError,
    error: policiesQuery.error,
    refetch: policiesQuery.refetch,
    searchQuery,
    setSearchQuery,
    employeeGroupFilter,
    setEmployeeGroupFilter,
    statusFilter,
    setStatusFilter,
    totalAssignedPersonnel,
    handleClearFilters,
    createPolicyMutation,
    stageAttachmentMutation,
    staffTypeOptions,
    isStaffTypesLoading,
    isStaffTypesError,
    staffTypesError,
    refetchStaffTypes,
  }
}

/**
 * Fetch a single Leave Policy by ID for edit/view.
 * Query key includes branch id + policy id.
 * staleTime is 0 and refetchOnMount is 'always'.
 */
export function useLeavePolicyDetail(policyId?: string | null, enabled: boolean = true) {
  const branchId = resolveBranchId() || ''

  return useQuery({
    queryKey: getHrmsLeavePolicyDetailQueryKey(policyId, branchId),
    queryFn: () => {
      if (!policyId) throw new Error('Policy ID is required')
      return getLeavePolicyById(policyId)
    },
    enabled: Boolean(enabled && policyId),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * Mutation hook for updating a Leave Policy.
 */
export function useUpdateLeavePolicy() {
  const queryClient = useQueryClient()
  const branchId = resolveBranchId() || ''

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateLeavePolicyPayload }) =>
      updateLeavePolicy(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: HRMS_LEAVE_POLICIES_QUERY_KEY })
      queryClient.invalidateQueries({
        queryKey: getHrmsLeavePolicyDetailQueryKey(variables.id, branchId),
      })
    },
  })
}
